import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { io } from "socket.io-client";
import DogBotIcon from "./DogBotIcon";

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || `http://${window.location.hostname}:5000`;

// ─── QUICK SUGGESTION CHIPS ──────────────────────────────────────────────────
const QUICK_PROMPTS_EN = [
  { label: "🐾 Grooming Services & Rates", query: "What are your grooming services and prices?" },
  { label: "🏨 Pet Hotel Boarding", query: "Tell me about your pet hotel rooms and rates" },
  { label: "🍖 Products & Treats", query: "What pet food and products do you sell?" },
  { label: "📅 How to Book", query: "How do I book an appointment?" },
  { label: "⏰ Store Hours & Location", query: "What are your hours and address?" },
  { label: "💳 Payment Methods", query: "What payment methods and downpayment do you accept?" },
  { label: "🧑‍💼 Talk to Live Admin", query: "Can I speak to a live human admin?" },
];

const QUICK_PROMPTS_TL = [
  { label: "🐾 Grooming at Presyo", query: "Ano ang mga grooming services at presyo?" },
  { label: "🏨 Pet Hotel Boarding", query: "Magkano at ano ang pet hotel rooms ninyo?" },
  { label: "🍖 Mga Produkto at Pagkain", query: "Anong mga produkto at pagkain ang tinda ninyo?" },
  { label: "📅 Paano Mag-book", query: "Paano magpa-book ng appointment?" },
  { label: "⏰ Oras at Lokasyon", query: "Ano ang oras at lokasyon ng shop?" },
  { label: "💳 Paraan ng Pagbabayad", query: "Paano ang bayad at downpayment?" },
  { label: "🧑‍💼 Kausapin ang Admin", query: "Gusto kong makausap ang live admin" },
];

// ─── STRICT TOPIC DETECTION HELPER ──────────────────────────────────────────
const isFurpectTailsTopic = (text) => {
  const t = text.toLowerCase();

  const allowedKeywords = [
    // Brand & greetings
    "furbot", "furpect", "furpect tails", "furpecttails", "hi", "hello", "hey", "kumusta", "kamusta", "good morning", "good afternoon", "good evening", "salamat", "thank", "thanks", "bye", "goodbye", "help", "tulong", "info", "about",
    // Pets & Animals
    "pet", "pets", "dog", "dogs", "puppy", "puppies", "cat", "cats", "kitten", "kittens", "aspin", "puspin", "breed", "shih tzu", "poodle", "husky", "golden retriever", "labrador", "persian", "siamese", "alaga", "hayop", "aso", "pusa",
    // Grooming & Styling
    "groom", "grooming", "bath", "paligo", "ligalig", "blow dry", "brush", "haircut", "cut", "styling", "gupit", "tabas", "teddy bear", "puppy cut", "lion cut", "summer cut", "comb cut", "natural trim", "nail", "kuko", "teeth", "ngipin", "eye wash", "ear", "tainga", "tenga", "anal drain", "sanitary", "flea", "tick", "kuto", "medicated",
    // Hotel & Boarding
    "hotel", "boarding", "board", "overnight", "stay", "room", "villa", "condo", "tulog", "iwan", "alaga habang wala", "check in", "check-in", "check out", "check-out", "daycare", "cage", "supervision", "feeding",
    // Products & Food
    "product", "products", "item", "items", "food", "treat", "treats", "biscuit", "biscuits", "freeze dried", "cubes", "dentastix", "goodest", "aozi", "whiskas", "temptation", "temptations", "licky", "beef bites", "soap", "herbal", "spray", "cream", "lc vit", "vitamins", "gamot", "tinda", "benta", "bili", "shampoo",
    // Booking & Appointments
    "book", "booking", "appointment", "reserve", "reservation", "schedule", "iskedyul", "slot", "availability", "time", "date", "reschedule", "cancel", "kansela", "palit ng date", "walk in", "walk-in",
    // Payment & Account
    "pay", "payment", "bayad", "price", "prices", "rate", "rates", "cost", "magkano", "presyo", "downpayment", "down payment", "deposit", "gcash", "unionbank", "bdo", "bank", "cash", "reference", "ref no", "receipt", "account", "login", "register", "signup", "qr", "qr code", "profile", "password", "otp", "verify",
    // Store Info & Safety
    "hour", "hours", "open", "close", "schedule", "oras", "bukas", "sarado", "sunday", "linggo", "weekend", "location", "address", "saan", "lugar", "map", "contact", "phone", "email", "telepono", "tawag", "message", "vaccine", "vaccination", "bakuna", "rabies", "anti-rabies", "requirements", "policy", "safe", "safety", "rules", "staff", "admin", "live chat", "human", "support", "tao"
  ];

  return allowedKeywords.some((kw) => t.includes(kw));
};

const Chatbot = ({ setActiveTab, user }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [language, setLanguage] = useState("en");
  const [messages, setMessages] = useState([
    {
      id: 1,
      sender: "bot",
      text: "Hi there! 👋 I'm FurBot, your dedicated Furpect Tails assistant! 🐾 I can help you with grooming services, pet hotel boarding, pet treats & supplies, pricing, appointments, and shop info. How can I help your furry baby today?",
      time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
    },
  ]);
  const [input, setInput] = useState("");
  const [isLiveChat, setIsLiveChat] = useState(false);
  const [adminOnline, setAdminOnline] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const socketRef = useRef(null);
  const sessionIdRef = useRef(`session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);

  // ── Drag & Move State for FAB ──
  const [fabPos, setFabPos] = useState(() => {
    try {
      const saved = sessionStorage.getItem("furbot_fab_pos");
      if (saved) {
        const parsed = JSON.parse(saved);
        if (typeof parsed.x === "number" && typeof parsed.y === "number") {
          return parsed;
        }
      }
    } catch (e) {
      // ignore
    }
    const isMobile = typeof window !== "undefined" && window.innerWidth < 640;
    const initialX = typeof window !== "undefined" ? window.innerWidth - (isMobile ? 76 : 88) : 500;
    const initialY = typeof window !== "undefined" ? window.innerHeight - (isMobile ? 76 : 88) : 500;
    return { x: initialX, y: initialY };
  });

  const [isFabDragging, setIsFabDragging] = useState(false);
  const fabDragRef = useRef({ startX: 0, startY: 0, startPosX: 0, startPosY: 0, moved: false });

  // ── Drag & Move State for Chat Window ──
  const [chatPos, setChatPos] = useState(null); // null = auto relative to FAB
  const [isChatDragging, setIsChatDragging] = useState(false);
  const chatDragRef = useRef({ startX: 0, startY: 0, startPosX: 0, startPosY: 0, moved: false });

  // Listen for external triggers to open FurBot (e.g. from FAQ or Contact pages)
  useEffect(() => {
    const handleOpenFurbot = () => {
      setIsOpen(true);
    };
    window.addEventListener("open-furbot", handleOpenFurbot);
    return () => window.removeEventListener("open-furbot", handleOpenFurbot);
  }, []);

  // Save FAB position
  useEffect(() => {
    try {
      sessionStorage.setItem("furbot_fab_pos", JSON.stringify(fabPos));
    } catch (e) {
      // ignore
    }
  }, [fabPos]);

  // Window Resize Clamping
  useEffect(() => {
    const handleResize = () => {
      const btnSize = 64;
      const margin = 12;
      setFabPos((prev) => ({
        x: Math.max(margin, Math.min(prev.x, window.innerWidth - btnSize - margin)),
        y: Math.max(margin, Math.min(prev.y, window.innerHeight - btnSize - margin)),
      }));
      setChatPos((prev) => {
        if (!prev) return null;
        const chatWidth = Math.min(390, window.innerWidth - 24);
        const chatHeight = Math.min(520, window.innerHeight - 40);
        return {
          x: Math.max(margin, Math.min(prev.x, window.innerWidth - chatWidth - margin)),
          y: Math.max(margin, Math.min(prev.y, window.innerHeight - chatHeight - margin)),
        };
      });
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Compute smart Chat Window position relative to FAB
  const getComputedChatPos = useCallback(() => {
    if (chatPos) return chatPos;
    const chatWidth = Math.min(390, (typeof window !== "undefined" ? window.innerWidth : 400) - 24);
    const chatHeight = Math.min(520, (typeof window !== "undefined" ? window.innerHeight : 600) - 40);
    const margin = 12;
    const winWidth = typeof window !== "undefined" ? window.innerWidth : 1000;
    const winHeight = typeof window !== "undefined" ? window.innerHeight : 800;

    // Prefer placing above FAB
    let y = fabPos.y - chatHeight - 12;
    if (y < margin) {
      // If not enough room above, place below FAB
      y = fabPos.y + 64 + 12;
    }

    // Align right edges of chat and FAB
    let x = fabPos.x + 64 - chatWidth;
    if (x < margin) {
      x = fabPos.x;
    }

    // Clamp within screen
    x = Math.max(margin, Math.min(x, winWidth - chatWidth - margin));
    y = Math.max(margin, Math.min(y, winHeight - chatHeight - margin));

    return { x, y };
  }, [chatPos, fabPos]);

  // FAB Drag Handlers (pointer/mouse/touch)
  const handleFabPointerDown = (e) => {
    if (e.button !== undefined && e.button !== 0) return;
    const clientX = e.clientX ?? e.touches?.[0]?.clientX;
    const clientY = e.clientY ?? e.touches?.[0]?.clientY;
    if (clientX === undefined || clientY === undefined) return;

    fabDragRef.current = {
      startX: clientX,
      startY: clientY,
      startPosX: fabPos.x,
      startPosY: fabPos.y,
      moved: false,
    };

    const handlePointerMove = (moveEvent) => {
      const curX = moveEvent.clientX ?? moveEvent.touches?.[0]?.clientX;
      const curY = moveEvent.clientY ?? moveEvent.touches?.[0]?.clientY;
      if (curX === undefined || curY === undefined) return;

      const dx = curX - fabDragRef.current.startX;
      const dy = curY - fabDragRef.current.startY;

      if (!fabDragRef.current.moved && Math.hypot(dx, dy) > 4) {
        fabDragRef.current.moved = true;
        setIsFabDragging(true);
      }

      if (fabDragRef.current.moved) {
        const btnSize = 64;
        const margin = 10;
        const newX = Math.max(margin, Math.min(fabDragRef.current.startPosX + dx, window.innerWidth - btnSize - margin));
        const newY = Math.max(margin, Math.min(fabDragRef.current.startPosY + dy, window.innerHeight - btnSize - margin));
        setFabPos({ x: newX, y: newY });
      }
    };

    const handlePointerUp = () => {
      window.removeEventListener("mousemove", handlePointerMove);
      window.removeEventListener("mouseup", handlePointerUp);
      window.removeEventListener("touchmove", handlePointerMove);
      window.removeEventListener("touchend", handlePointerUp);
      setTimeout(() => {
        setIsFabDragging(false);
      }, 60);
    };

    window.addEventListener("mousemove", handlePointerMove, { passive: false });
    window.addEventListener("mouseup", handlePointerUp);
    window.addEventListener("touchmove", handlePointerMove, { passive: false });
    window.addEventListener("touchend", handlePointerUp);
  };

  const handleFabClick = (e) => {
    if (fabDragRef.current.moved) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    setIsOpen((prev) => !prev);
  };

  // Chat Window Header Drag Handlers
  const handleHeaderPointerDown = (e) => {
    if (e.target.closest("button") || e.target.closest("input") || e.target.closest("a")) return;
    if (e.button !== undefined && e.button !== 0) return;

    const clientX = e.clientX ?? e.touches?.[0]?.clientX;
    const clientY = e.clientY ?? e.touches?.[0]?.clientY;
    if (clientX === undefined || clientY === undefined) return;

    const initialPos = getComputedChatPos();
    chatDragRef.current = {
      startX: clientX,
      startY: clientY,
      startPosX: initialPos.x,
      startPosY: initialPos.y,
      moved: false,
    };
    setIsChatDragging(true);

    const handleChatMove = (moveEvent) => {
      const curX = moveEvent.clientX ?? moveEvent.touches?.[0]?.clientX;
      const curY = moveEvent.clientY ?? moveEvent.touches?.[0]?.clientY;
      if (curX === undefined || curY === undefined) return;

      const dx = curX - chatDragRef.current.startX;
      const dy = curY - chatDragRef.current.startY;

      const chatWidth = Math.min(390, window.innerWidth - 24);
      const chatHeight = Math.min(520, window.innerHeight - 40);
      const margin = 10;

      const newX = Math.max(margin, Math.min(chatDragRef.current.startPosX + dx, window.innerWidth - chatWidth - margin));
      const newY = Math.max(margin, Math.min(chatDragRef.current.startPosY + dy, window.innerHeight - chatHeight - margin));

      setChatPos({ x: newX, y: newY });
    };

    const handleChatUp = () => {
      window.removeEventListener("mousemove", handleChatMove);
      window.removeEventListener("mouseup", handleChatUp);
      window.removeEventListener("touchmove", handleChatMove);
      window.removeEventListener("touchend", handleChatUp);
      setIsChatDragging(false);
    };

    window.addEventListener("mousemove", handleChatMove, { passive: false });
    window.addEventListener("mouseup", handleChatUp);
    window.addEventListener("touchmove", handleChatMove, { passive: false });
    window.addEventListener("touchend", handleChatUp);
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (isOpen) {
      scrollToBottom();
    }
  }, [messages, isTyping, isOpen]);

  // ── Bulletproof Navigation Helper ──
  const navigateToTab = useCallback((tab) => {
    if (!setActiveTab) return;
    if (tab === "booking") {
      setActiveTab("booking");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else if (tab === "products") {
      setActiveTab("products");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else if (tab === "services") {
      setActiveTab("home");
      setTimeout(() => {
        const el = document.getElementById("services");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        else window.scrollTo({ top: 700, behavior: "smooth" });
      }, 120);
    } else if (tab === "about") {
      setActiveTab("home");
      setTimeout(() => {
        const el = document.getElementById("about");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 120);
    } else if (tab === "contact") {
      setActiveTab("contact");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else if (tab === "faq") {
      setActiveTab("faq");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      setActiveTab(tab);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [setActiveTab]);

  // ── Socket.io Live Chat Connection ──
  const connectSocket = useCallback((initialUserMessage = null) => {
    if (socketRef.current) return;
    const socket = io(SOCKET_URL);
    socketRef.current = socket;

    const sessionId = sessionIdRef.current;
    const userName = user?.name || "Guest";

    socket.emit("user_join_chat", { sessionId, userName });

    if (initialUserMessage) {
      socket.emit("send_message", {
        sessionId,
        message: initialUserMessage,
        sender: "user",
      });
    }

    socket.on("admin_joined", () => {
      setAdminOnline(true);
      addBotMessage(
        language === "tl"
          ? "Ang aming live admin ay nakakonekta na! Maaari ka nang mag-type ng iyong katanungan. 🐾"
          : "A Furpect Tails live admin has joined the chat! You can now ask your questions. 🐾"
      );
    });

    socket.on("receive_message", ({ text, sender, time }) => {
      if (sender === "admin") {
        setMessages((prev) => [
          ...prev,
          {
            id: Date.now(),
            sender: "admin",
            text,
            time: new Date(time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
          },
        ]);
      }
    });

    socket.on("user_disconnected", () => {
      setAdminOnline(false);
    });
  }, [user, language]);

  const disconnectSocket = () => {
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }
    setIsLiveChat(false);
    setAdminOnline(false);
  };

  useEffect(() => {
    return () => disconnectSocket();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const addBotMessage = (text, suggestedTab = null) => {
    setMessages((prev) => [
      ...prev,
      {
        id: Date.now(),
        sender: "bot",
        text,
        suggestedTab,
        time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
      },
    ]);
  };

  // ─── LOCAL FALLBACK KNOWLEDGE ENGINE ─────────────────────────────────────────
  const generateBotResponse = (userText, lang) => {
    const text = userText.toLowerCase().trim();
    let isFallback = false;
    let responseText = "";
    let suggestedTab = null;

    // 1. STRICT SCOPE CHECK
    if (!isFurpectTailsTopic(text)) {
      if (lang === "tl") {
        return {
          text: "Ako si FurBot, ang AI assistant na eksklusibo para sa Furpect Tails! 🐾\n\nNandito ako para sagutin ang mga tanong tungkol sa aming mga serbisyo (Grooming, Pet Hotel), mga produkto at treats, presyo, schedule, lokasyon, at booking para sa inyong mga aso't pusa.\n\nPaano ko matutulungan ang inyong alaga ngayon?",
          isFallback: false,
          suggestedTab: null,
        };
      } else {
        return {
          text: "I am FurBot, dedicated exclusively to Furpect Tails! 🐾\n\nI can only answer questions related to Furpect Tails services (Grooming, Pet Hotel Boarding), pet products & treats, pricing, business hours, location, and appointments for your dogs and cats.\n\nHow can I help you with your pet today?",
          isFallback: false,
          suggestedTab: null,
        };
      }
    }

    // 2. LIVE HUMAN ADMIN REQUEST
    if (
      text.includes("human") || text.includes("admin") || text.includes("staff") ||
      text.includes("agent") || text.includes("person") || text.includes("kausapin ang admin") ||
      text.includes("tao") || text.includes("live support") || text.includes("live chat")
    ) {
      isFallback = true;
      responseText = lang === "tl"
        ? "Ikinokonekta na kita sa isang Furpect Tails live admin/staff. Mangyaring maghintay sandali... 🐾"
        : "Connecting you directly to a Furpect Tails live admin/staff member. Please wait a moment... 🐾";
      return { text: responseText, isFallback, suggestedTab: null };
    }

    // 3. TAGALOG RESPONSES
    if (lang === "tl") {
      if (text.match(/^(hi|hello|hey|kumusta|kamusta|good morning|good afternoon|good evening|magandang araw)/)) {
        responseText = "Kumusta! 🐾 Ako si FurBot ng Furpect Tails. Matutulungan kita sa grooming packages, pet hotel boarding, mga pagkain/treats, presyo, at booking. Anong serbisyo ang kailangan ng iyong alaga?";
      } else if (text.includes("groom") || text.includes("paligo") || text.includes("gupit") || text.includes("tabas") || text.includes("haircut") || text.includes("bath")) {
        suggestedTab = "booking";
        if (text.includes("pusa") || text.includes("cat")) {
          responseText = "🐱 Cat Grooming Rates:\n• Full Cat Grooming: ₱600 (Kasama ang paligo, blowdry, nail trim, ear cleaning, at sanitary trim / gupit).\n\nGusto mo bang mag-book ng Cat Grooming?";
        } else {
          responseText = "🐶 Dog & Cat Grooming Packages:\n\n1. Basic Grooming (Paligo, blowdry, brush, nail trim):\n• Small (Single Coated): ₱400\n• Medium (Single Coated): ₱500\n• Large (Double Coated): ₱650\n• Extra Large (Triple Coated): ₱850\n\n2. Full Grooming (Basic + Haircut & Styling):\n• Small: ₱500 | Medium: ₱600 | Large: ₱750 | Extra Large: ₱950\n\n3. Cat Grooming: ₱600 flat rate";
        }
      } else if (text.includes("coat") || text.includes("balahibo") || text.includes("single coat") || text.includes("double coat") || text.includes("triple coat") || text.includes("makapal")) {
        suggestedTab = "services";
        responseText = "🐕 Gabay sa Dog Coat Types & Grooming:\n\n1. Single Coated (Small & Medium • ₱400-₱600):\n• Isang layer ng balahibo, walang makapal na undercoat (Shih Tzu, Poodle, Yorkie, French Bulldog, Chihuahua, Pug).\n\n2. Double Coated (Large • ₱650-₱750):\n• Dalawang layers: Outer guard coat + makapal na insulating undercoat na nag-she-shed (Golden Retriever, Husky, Labrador, German Shepherd, Corgi, Pomeranian).\n\n3. Triple Coated (Extra Large • ₱850-₱950):\n• Napakakapal at napakaraming volume ng balahibo (Chow Chow, Samoyed, Alaskan Malamute, St. Bernard).\n\n💡 Tinitingnan din ng aming groomers ang alaga pagdating sa clinic para masigurado ang tamang package!";
      } else if (text.includes("addon") || text.includes("kuko") || text.includes("nail") || text.includes("ngipin") || text.includes("teeth") || text.includes("ear") || text.includes("tainga") || text.includes("tenga") || text.includes("anal")) {
        suggestedTab = "booking";
        responseText = "✨ Add-on Services:\n• Nail Trim: ₱100\n• Teeth Brushing: ₱100\n• Eye Wash: ₱100\n• Ear Cleaning: ₱100\n• Anal Drain: ₱180\n\nMaaari mong idagdag ang mga ito kapag nag-book ka ng grooming!";
      } else if (text.includes("style") || text.includes("teddy bear") || text.includes("puppy cut") || text.includes("lion cut") || text.includes("summer cut") || text.includes("comb cut")) {
        suggestedTab = "booking";
        responseText = "✂️ Mga Haircut Styles na Available:\n• Para sa Aso: Teddy Bear Cut, Puppy Cut, Lion Cut, Summer Cut, Natural Trim, at Breed-Specific Cuts.\n• Para sa Pusa: Lion Cut, Summer Cut, Comb Cut, at Natural Trim.\n\nMaaari kang pumili ng style sa aming Booking Form!";
      } else if (text.includes("hotel") || text.includes("board") || text.includes("overnight") || text.includes("tulog") || text.includes("iwan") || text.includes("stay")) {
        suggestedTab = "booking";
        responseText = "🏨 Furpect Tails Luxury Pet Hotel:\n\n• Dog Room Small (maliit at medium na aso): ₱500 / gabi (Capacity: 6)\n• Dog Room Large (malalaking aso): ₱800 / gabi (Capacity: 2)\n• Cat Villa Small (cozy private villa para sa pusa): ₱500 / gabi (Capacity: 4)\n• Cat Villa Large (spacious luxury villa para sa pusa): ₱800 / gabi (Capacity: 2)\n\n🕒 Check-in: 10:00 AM, 2:00 PM, 4:00 PM\n🕒 Check-out: 10:00 AM, 12:00 PM, 2:00 PM\n⭐ Kasama ang 24/7 supervision, feeding schedule, at daily playtime updates!";
      } else if (text.includes("product") || text.includes("treat") || text.includes("pagkain") || text.includes("food") || text.includes("biscuit") || text.includes("bili") || text.includes("tinda") || text.includes("shampoo") || text.includes("vitamins") || text.includes("soap") || text.includes("diaper") || text.includes("cologne") || text.includes("toy")) {
        suggestedTab = "products";
        responseText = "🍖 Mga Produkto at Treats sa Furpect Tails:\n\n🐶 Para sa Aso:\n• Milky Biscuit (80g ₱95 / 200g ₱170)\n• FP Freeze Dried Chicken & Egg Yolk Cubes (50g ₱95)\n• Doggie Biscuit (80g ₱95 / 200g ₱170)\n• Dentastix Dog Treats (₱120 - ₱135)\n• Goodest Dog Wet Food (130g ₱55 / 80g ₱45)\n• Jumbone Pedigree (₱120)\n• Furrpole Beef Bites (500g ₱120)\n\n🐱 Para sa Pusa:\n• Temptations Treats (75g ₱135)\n• Goodest Cat (85g ₱45 / Tasty Tuna 100g ₱120)\n• Cat Treat Premium Licky Sticks (₱160)\n• Whiskas (80g ₱40)\n\n🧴 Pet Essentials & Care:\n• FP Pet Cologne 100ml (₱95)\n• FP Shampoo 1L (₱100)\n• Male & Female Diapers (XS ₱15 / Small-Med ₱20 / L-XL ₱25)\n• Pet Toothpaste 100g (₱120) & Toothbrush (₱35)\n• MDC Leaf Extract Spray (₱130)\n• MDC Healing Cream (₱90)\n• MDC Herbal Soap (₱60) & Ointment (₱50)\n• LC-Vit Syrup (₱120) & Pet Toys (₱90)";
      } else if (text.includes("paano mag-book") || text.includes("how to book") || text.includes("book") || text.includes("appointment") || text.includes("schedule") || text.includes("iskedyul") || text.includes("reserve")) {
        suggestedTab = "booking";
        responseText = "📅 Madali lang mag-book sa Furpect Tails:\n1. Pindutin ang 'Book Appointment' sa menu.\n2. Piliin ang serbisyo (Grooming, Pet Hotel, o Pareho).\n3. Ilagay ang impormasyon ng iyong alaga at pumili ng oras/petsa.\n4. Pumili ng payment method (10% Downpayment o Full Payment via GCash, UnionBank, BDO, o Cash).\n5. Pirmahan ang terms at isumite!";
      } else if (text.includes("pay") || text.includes("bayad") || text.includes("presyo") || text.includes("magkano") || text.includes("gcash") || text.includes("unionbank") || text.includes("bdo") || text.includes("bank") || text.includes("cash") || text.includes("downpayment") || text.includes("mop")) {
        responseText = "💳 Paraan ng Pagbabayad sa Furpect Tails:\n• Tumatanggap kami ng GCash, UnionBank, BDO, at Cash sa shop.\n• May opsyon kang magbayad ng 10% Downpayment para ma-reserve ang iyong slot, o bayaran nang buo.\n• Kung Cash ang pipiliin, hindi na kailangan ng online reference number.";
      } else if (text.includes("hour") || text.includes("oras") || text.includes("bukas") || text.includes("sarado") || text.includes("open") || text.includes("close") || text.includes("sunday") || text.includes("linggo")) {
        suggestedTab = "contact";
        responseText = "⏰ Oras ng Operasyon ng Furpect Tails:\n• Martes–Linggo: 9:00 AM – 7:00 PM\n• Lunes: SARADO (Puwede pa rin ang hotel check-out sa Lunes)\n\n📍 Lokasyon at Address:\n• 96 Gen. Luna St., Tuktukan, Taguig City\n📞 Telepono: 0954 498 8342\n✉️ Email: furpecttails@gmail.com\n\nInirerekomenda namin ang pag-book nang maaga para masigurado ang slot ng inyong alaga!";
      } else if (text.includes("location") || text.includes("saan") || text.includes("address") || text.includes("lugar") || text.includes("map") || text.includes("contact") || text.includes("tawag") || text.includes("telepono")) {
        suggestedTab = "contact";
        responseText = "📍 Lokasyon at Kontak ng Furpect Tails:\n• Address: 96 Gen. Luna St., Tuktukan, Taguig City\n• Telepono: 0954 498 8342\n• Email: furpecttails@gmail.com\n\n⏰ Oras ng Operasyon:\n• Martes–Linggo: 9:00 AM – 7:00 PM\n• Lunes: SARADO (Puwede pa rin ang hotel check-out sa Lunes)\n\nMaaari kang magpadala ng mensahe sa aming Contact Form o gamitin ang Live Support dito.";
      } else if (text.includes("vaccine") || text.includes("bakuna") || text.includes("rabies") || text.includes("requirements") || text.includes("policy") || text.includes("kuto") || text.includes("flea")) {
        responseText = "💉 Mahigpit na Patakaran sa Bakuna:\n• Mahigpit na KINAKAILANGAN ang updated vaccine record (lalo na ang Anti-Rabies). Ang mga aso at pusang HINDI bakunado ay HINDI tatanggapin sa tindahan/shop para sa grooming o hotel.\n• Para sa mga alagang may kuto o ticks, mayroon kaming medicated bath at tick treatments.\n• Hinihiling namin na ipagbigay-alam sa amin kung may aggressive behavior o medical needs ang inyong alaga.";
      } else if (text.includes("cancel") || text.includes("reschedule") || text.includes("palit") || text.includes("kansela")) {
        responseText = "🔄 Rescheduling & Cancellation:\n• Madali kang makakapag-request ng reschedule o cancellation direkta mula sa iyong Customer Dashboard.\n• Ang admin ay magpapadala ng kumpirmasyon sa iyong request sa lalong madaling panahon.";
      } else if (text.includes("qr") || text.includes("account") || text.includes("profile") || text.includes("register")) {
        responseText = "📱 Pet QR & Account:\n• Bawat rehistradong alaga sa iyong Furpect Tails profile ay may sariling Pet QR Code!\n• I-scan lamang ito ng aming staff sa shop para sa mabilisang check-in at pagtingin sa medical history ng iyong alaga.";
      } else if (["yes", "sure", "ok", "yep", "yeah", "please", "alright", "opo", "oo", "sige", "sige po", "punta tayo"].some((w) => text === w || text.startsWith(w + " "))) {
        responseText = "Ayos! Dinadala na kita sa Booking Page ngayon! 🐾";
        suggestedTab = "booking";
      } else if (text.includes("salamat") || text.includes("thank") || text.includes("bye") || text.includes("paalam")) {
        responseText = "Walang anuman! Masaya kaming makatulong sa inyo at sa inyong alaga. Ingat at kita-kits sa Furpect Tails! 🐾✨";
      } else {
        responseText = "Matutulungan kita sa mga serbisyo ng Furpect Tails tulad ng Grooming, Pet Hotel, mga Produkto/Treats, Presyo, at Booking. Maaari mo ring pindutin ang mga suggestion buttons sa ibaba o hilinging kumausap sa live admin! 🐾";
      }
    }
    // 4. ENGLISH RESPONSES
    else {
      if (text.match(/^(hi|hello|hey|good morning|good afternoon|good evening|greetings)/)) {
        responseText = "Hello! 🐾 I'm FurBot from Furpect Tails. I'm here to assist you with grooming packages, pet hotel boarding, treats & products, pricing, and appointments. What does your pet need today?";
      } else if (text.includes("groom") || text.includes("bath") || text.includes("haircut") || text.includes("wash") || text.includes("styling") || text.includes("nail")) {
        suggestedTab = "booking";
        if (text.includes("cat")) {
          responseText = "🐱 Cat Grooming Service:\n• Full Cat Grooming: ₱600 flat rate (Includes full bath, blowdry, nail trimming, ear cleaning, and styling / sanitary trim).";
        } else {
          responseText = "🐶 Dog & Cat Grooming Packages:\n\n1. Basic Grooming (Bath, blowdry, brush, nail trim):\n• Small (Single Coated): ₱400\n• Medium (Single Coated): ₱500\n• Large (Double Coated): ₱650\n• Extra Large (Triple Coated): ₱850\n\n2. Full Grooming (Basic + Haircut & Styling):\n• Small: ₱500 | Medium: ₱600 | Large: ₱750 | Extra Large: ₱950\n\n3. Cat Grooming: ₱600 flat rate";
        }
      } else if (text.includes("coat") || text.includes("single coat") || text.includes("double coat") || text.includes("triple coat") || text.includes("fur type")) {
        suggestedTab = "services";
        responseText = "🐕 Dog Coat Type & Sizing Guide:\n\n1. Single Coated (Small & Medium • ₱400-₱600):\n• 1 Layer of hair with no dense undercoat (e.g. Shih Tzu, Poodle, Yorkie, French Bulldog, Chihuahua, Pug).\n\n2. Double Coated (Large • ₱650-₱750):\n• 2 Layers: Weather-resistant topcoat + thick insulating undercoat that sheds seasonally (e.g. Golden Retriever, Husky, Labrador, German Shepherd, Corgi, Pomeranian).\n\n3. Triple Coated (Extra Large • ₱850-₱950):\n• Exceptionally dense, heavy multi-layered volume coats (e.g. Chow Chow, Samoyed, Alaskan Malamute, St. Bernard).\n\n💡 Our groomers always evaluate your pet's size and coat density upon check-in to confirm the exact best package!";
      } else if (text.includes("addon") || text.includes("add-on") || text.includes("nail trim") || text.includes("teeth") || text.includes("eye wash") || text.includes("ear clean") || text.includes("anal drain")) {
        suggestedTab = "booking";
        responseText = "✨ Add-on Services Available:\n• Nail Trim: ₱100\n• Teeth Brushing: ₱100\n• Eye Wash: ₱100\n• Ear Cleaning: ₱100\n• Anal Drain: ₱180\n\nYou can easily select any of these add-ons during your online booking!";
      } else if (text.includes("style") || text.includes("teddy bear") || text.includes("puppy cut") || text.includes("lion cut") || text.includes("summer cut") || text.includes("comb cut")) {
        suggestedTab = "booking";
        responseText = "✂️ Available Haircut Styles:\n• For Dogs: Teddy Bear Cut, Puppy Cut, Lion Cut, Summer Cut, Natural Trim, and Breed-Specific Cuts.\n• For Cats: Lion Cut, Summer Cut, Comb Cut, and Natural Trim.\n\nYou can select your desired style during booking!";
      } else if (text.includes("hotel") || text.includes("board") || text.includes("overnight") || text.includes("stay") || text.includes("room") || text.includes("villa")) {
        suggestedTab = "booking";
        responseText = "🏨 Furpect Tails Luxury Pet Hotel:\n\n• Dog Room Small (Small & Medium dogs): ₱500 / night (Capacity: 6)\n• Dog Room Large (Large dogs): ₱800 / night (Capacity: 2)\n• Cat Villa Small (Cozy private villa for cats): ₱500 / night (Capacity: 4)\n• Cat Villa Large (Spacious luxury villa for cats): ₱800 / night (Capacity: 2)\n\n🕒 Check-in Times: 10:00 AM, 2:00 PM, 4:00 PM\n🕒 Check-out Times: 10:00 AM, 12:00 PM, 2:00 PM\n⭐ Includes 24/7 dedicated pet care supervision, custom feeding, and playtime updates!";
      } else if (text.includes("product") || text.includes("treat") || text.includes("food") || text.includes("biscuit") || text.includes("buy") || text.includes("shop") || text.includes("shampoo") || text.includes("vitamins") || text.includes("soap") || text.includes("diaper") || text.includes("cologne") || text.includes("toy")) {
        suggestedTab = "products";
        responseText = "🍖 Furpect Tails Products & Treats Catalog:\n\n🐶 For Dogs:\n• Milky Biscuit (80g ₱95 / 200g ₱170)\n• FP Freeze Dried Chicken & Egg Yolk Cubes (50g ₱95)\n• Doggie Biscuit (80g ₱95 / 200g ₱170)\n• Dentastix Dog Treats (₱120 - ₱135)\n• Goodest Dog Wet Food (130g ₱55 / 80g ₱45)\n• Jumbone Pedigree (₱120)\n• Furrpole Beef Bites (500g ₱120)\n\n🐱 For Cats:\n• Temptations Treats (75g ₱135)\n• Goodest Cat (85g ₱45 / Tasty Tuna 100g ₱120)\n• Cat Treat Premium Licky Sticks (₱160)\n• Whiskas (80g ₱40)\n\n🧴 Pet Essentials & Care:\n• FP Pet Cologne 100ml (₱95)\n• FP Shampoo 1L (₱100)\n• Male & Female Diapers (XS ₱15 / Small-Med ₱20 / L-XL ₱25)\n• Pet Toothpaste 100g (₱120) & Toothbrush (₱35)\n• MDC Pure Leaf Extract Spray (₱130)\n• MDC Topical Healing Cream (₱90)\n• MDC Herbal Soap (₱60) & Ointment (₱50)\n• LC-Vit Syrup (₱120) & Pet Toys (₱90)";
      } else if (text.includes("how to book") || text.includes("book") || text.includes("appointment") || text.includes("schedule") || text.includes("reserve")) {
        suggestedTab = "booking";
        responseText = "📅 Booking at Furpect Tails is quick and easy:\n1. Click 'Book Appointment' on the top menu.\n2. Choose your service (Grooming, Pet Hotel, or Both).\n3. Fill in your pet's details and choose an available date/time.\n4. Select your payment method (10% Downpayment or Full Payment via GCash, UnionBank, BDO, or in-store Cash).\n5. Sign the digital agreement and submit!";
      } else if (text.includes("pay") || text.includes("price") || text.includes("cost") || text.includes("rate") || text.includes("gcash") || text.includes("unionbank") || text.includes("bdo") || text.includes("bank") || text.includes("cash") || text.includes("downpayment") || text.includes("deposit")) {
        responseText = "💳 Payment Methods & Policy:\n• We accept GCash, UnionBank, BDO, and in-store Cash.\n• You can choose a 10% Downpayment to secure your appointment or pay in full.\n• When paying via Cash, an online reference number is not required.";
      } else if (text.includes("hour") || text.includes("open") || text.includes("close") || text.includes("schedule") || text.includes("time") || text.includes("sunday") || text.includes("monday") || text.includes("wednesday")) {
        suggestedTab = "contact";
        responseText = "⏰ Furpect Tails Operating Hours:\n• Tue–Sun: 9:00 AM – 7:00 PM\n• Monday: CLOSED (Hotel check-out is still allowed on Mondays)\n\n📍 Store Address:\n• 96 Gen. Luna St., Tuktukan, Taguig City\n📞 Phone: 0954 498 8342\n✉️ Email: furpecttails@gmail.com\n\nWe recommend booking in advance to guarantee your preferred time slot!";
      } else if (text.includes("location") || text.includes("where") || text.includes("address") || text.includes("map") || text.includes("contact") || text.includes("call") || text.includes("phone")) {
        suggestedTab = "contact";
        responseText = "📍 Furpect Tails Location & Contact:\n• Address: 96 Gen. Luna St., Tuktukan, Taguig City\n• Phone: 0954 498 8342\n• Email: furpecttails@gmail.com\n\n⏰ Operating Hours:\n• Tue–Sun: 9:00 AM – 7:00 PM\n• Monday: CLOSED (Hotel check-out is still allowed on Mondays)\n\nYou can reach out through our Contact page or message us directly on Live Chat.";
      } else if (text.includes("vaccine") || text.includes("rabies") || text.includes("requirements") || text.includes("policy") || text.includes("flea") || text.includes("tick")) {
        responseText = "💉 Strict Vaccination Policy:\n• Up-to-date vaccination records (especially Anti-Rabies) are STRICTLY REQUIRED. Pets that are not vaccinated will NOT be accepted in the store for grooming or hotel services.\n• We offer medicated baths for pets with active fleas or ticks.\n• Please inform us in advance if your pet has special behavioral or medical needs so our handlers can take extra care.";
      } else if (text.includes("cancel") || text.includes("reschedule") || text.includes("change date")) {
        responseText = "🔄 Rescheduling & Cancellations:\n• You can request a reschedule or cancellation directly from your Customer Dashboard.\n• Our administrative team reviews and updates all requests promptly.";
      } else if (text.includes("qr") || text.includes("account") || text.includes("profile") || text.includes("register") || text.includes("medical")) {
        responseText = "📱 Pet QR & Digital Profile:\n• Every registered pet in your account gets a unique Pet QR Code!\n• Staff can scan the QR code at the clinic for instant check-in, viewing service records and medical notes.";
      } else if (["yes", "sure", "ok", "yep", "yeah", "please", "alright", "take me there", "go"].some((w) => text === w || text.startsWith(w + " "))) {
        responseText = "Great! Opening the Booking Page now! 🐾";
        suggestedTab = "booking";
      } else if (text.includes("thank") || text.includes("thanks") || text.includes("bye") || text.includes("goodbye")) {
        responseText = "You're very welcome! We're always here for you and your pets. Have a wonderful day and see you at Furpect Tails! 🐾✨";
      } else {
        responseText = "I can help you with Furpect Tails grooming services, pet hotel boarding, treats & pet care products, pricing, and appointments. You can also tap any suggestion chip below or ask to connect with a live admin! 🐾";
      }
    }

    return { text: responseText, isFallback, suggestedTab };
  };

  const handleSend = async (e, customText = null) => {
    if (e) e.preventDefault();
    const queryText = (customText || input).trim();
    if (!queryText) return;

    const userMessage = {
      id: Date.now(),
      sender: "user",
      text: queryText,
      time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput("");

    // If in live chat mode, stream directly to socket
    if (isLiveChat) {
      if (socketRef.current) {
        socketRef.current.emit("send_message", {
          sessionId: sessionIdRef.current,
          message: userMessage.text,
          sender: "user",
        });
      }
      return;
    }

    setIsTyping(true);

    try {
      // Call backend Chatbot API with a natural typing delay (650ms) so animation is clearly visible
      const [res] = await Promise.all([
        axios.post("/api/chat", {
          message: userMessage.text,
          language,
          user,
        }),
        new Promise((resolve) => setTimeout(resolve, 650)),
      ]);

      setIsTyping(false);

      if (res.data?.success) {
        const { reply: botText, isFallback, targetTab, suggestedTab } = res.data;

        if (isFallback) {
          setIsLiveChat(true);
          addBotMessage(botText);
          connectSocket(userMessage.text);
          return;
        }

        const botResponse = {
          id: Date.now() + 1,
          sender: "bot",
          text: botText,
          suggestedTab: suggestedTab || null,
          time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
        };

        // If an explicit navigation command was detected, trigger smooth navigation
        if (targetTab) {
          navigateToTab(targetTab);
        }

        setMessages((prev) => [...prev, botResponse]);
      }
    } catch (err) {
      // Offline / network fallback with natural pause
      await new Promise((resolve) => setTimeout(resolve, 600));
      setIsTyping(false);
      const { text: botText, isFallback, suggestedTab } = generateBotResponse(userMessage.text, language);

      if (isFallback) {
        setIsLiveChat(true);
        addBotMessage(botText);
        connectSocket(userMessage.text);
        return;
      }

      const botResponse = {
        id: Date.now() + 1,
        sender: "bot",
        text: botText,
        suggestedTab: suggestedTab || null,
        time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
      };

      setMessages((prev) => [...prev, botResponse]);
    }
  };

  const handleEndLiveChat = () => {
    disconnectSocket();
    addBotMessage(
      language === "tl"
        ? "Natapos na ang live chat session. Salamat sa pakikipag-ugnayan sa Furpect Tails! Magtanong lamang kung may kailangan ka pa. 🐾"
        : "Live chat session ended. Thank you for contacting Furpect Tails! Feel free to ask me anything else about our services. 🐾"
    );
  };

  const promptChips = language === "tl" ? QUICK_PROMPTS_TL : QUICK_PROMPTS_EN;

  return (
    <>
      <style>{`
        @keyframes dog-bounce {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          25% { transform: translateY(-4px) rotate(-3deg); }
          75% { transform: translateY(-2px) rotate(3deg); }
        }
        @keyframes pulse-ring {
          0% { transform: scale(1); opacity: 0.7; }
          100% { transform: scale(1.6); opacity: 0; }
        }
        @keyframes pulse-ring2 {
          0% { transform: scale(1); opacity: 0.5; }
          100% { transform: scale(1.9); opacity: 0; }
        }
        @keyframes bubble-pop {
          0% { transform: scale(0.5) translateY(8px); opacity: 0; }
          60% { transform: scale(1.05) translateY(-2px); opacity: 1; }
          100% { transform: scale(1) translateY(0); opacity: 1; }
        }
        @keyframes chat-open {
          0% { transform: scale(0.92) translateY(12px); opacity: 0; }
          100% { transform: scale(1) translateY(0); opacity: 1; }
        }
        @keyframes message-in {
          0% { transform: translateY(8px) scale(0.97); opacity: 0; }
          100% { transform: translateY(0) scale(1); opacity: 1; }
        }
        @keyframes dot-pulse {
          0%, 80%, 100% { transform: translateY(0) scale(0.85); opacity: 0.5; }
          40% { transform: translateY(-5px) scale(1.15); opacity: 1; }
        }
        .message-anim { animation: message-in 0.22s cubic-bezier(0.34, 1.56, 0.64, 1) forwards; }
        .typing-dot { animation: dot-pulse 1.1s ease-in-out infinite; }
        .dog-bounce { animation: dog-bounce 2.4s ease-in-out infinite; }
        .pulse-ring {
          position: absolute; inset: -6px; border-radius: 9999px;
          border: 3px solid rgba(168,85,247,0.6);
          animation: pulse-ring 1.8s ease-out infinite;
        }
        .pulse-ring2 {
          position: absolute; inset: -6px; border-radius: 9999px;
          border: 2px solid rgba(236,72,153,0.4);
          animation: pulse-ring2 1.8s ease-out infinite 0.6s;
        }
        .chat-bubble-tooltip {
          animation: bubble-pop 0.4s cubic-bezier(0.34,1.56,0.64,1) forwards;
        }
        .chat-window-anim {
          animation: chat-open 0.25s cubic-bezier(0.34,1.56,0.64,1) forwards;
        }
        .fab-btn {
          transition: transform 0.25s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.25s ease;
        }
        .fab-btn:hover .dog-bounce { animation-play-state: paused; transform: scale(1.12); }
        .fab-btn:hover { box-shadow: 0 0 0 8px rgba(168,85,247,0.18), 0 8px 32px rgba(168,85,247,0.35); }
      `}</style>

      {/* Chat Window Container (Draggable anywhere on screen) */}
      {isOpen && (() => {
        const pos = getComputedChatPos();
        return (
          <div
            style={{
              position: "fixed",
              left: `${pos.x}px`,
              top: `${pos.y}px`,
              zIndex: 1000,
            }}
            className={`w-[calc(100vw-24px)] sm:w-[390px] h-[520px] max-h-[calc(100vh-32px)] chat-window-anim bg-white rounded-3xl shadow-2xl overflow-hidden border border-purple-100 flex flex-col ${
              isChatDragging ? "select-none shadow-purple-400/40 ring-4 ring-purple-300/60 transition-none" : "transition-all duration-75"
            }`}
          >
            {/* Draggable Header */}
            <div
              onMouseDown={handleHeaderPointerDown}
              onTouchStart={handleHeaderPointerDown}
              className={`p-4 flex items-center justify-between text-white select-none shadow-sm cursor-grab active:cursor-grabbing ${
                isLiveChat
                  ? "bg-gradient-to-r from-emerald-600 to-teal-600"
                  : "bg-gradient-to-r from-purple-700 via-purple-600 to-pink-600"
              }`}
              title="Drag here to move chat window"
            >
              <div className="flex items-center gap-3">
                <div className="relative w-11 h-11 flex-shrink-0 pointer-events-none">
                  {isLiveChat ? (
                    <div className="w-11 h-11 bg-white/20 rounded-full flex items-center justify-center shadow-md ring-2 ring-white/40">
                      <span className="text-xl">🧑‍💼</span>
                    </div>
                  ) : (
                    <DogBotIcon size={44} />
                  )}
                  {!isLiveChat && (
                    <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-400 border-2 border-white rounded-full z-10"></span>
                  )}
                </div>
                <div className="pointer-events-none">
                  <div className="flex items-center gap-1.5">
                    <h3 className="font-bold text-sm leading-tight">{isLiveChat ? "Furpect Live Support" : "FurBot AI"}</h3>
                    <span className="text-[9px] bg-white/20 px-1.5 py-0.5 rounded font-black tracking-wider uppercase">Furpect Only</span>
                  </div>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <span className={`w-2 h-2 rounded-full ${isLiveChat && adminOnline ? "bg-green-300 animate-pulse" : isLiveChat ? "bg-yellow-300 animate-pulse" : "bg-emerald-300"}`}></span>
                    <p className="text-[10px] text-white/90 uppercase tracking-wider font-bold">
                      {isLiveChat ? (adminOnline ? "Admin Connected" : "Waiting for Admin...") : "Pet Care Assistant"}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                {/* Drag Grip Indicator */}
                <div className="hidden sm:flex items-center text-white/50 hover:text-white/80 transition-colors mr-1 cursor-grab" title="Drag to move">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                    <circle cx="8" cy="6" r="2"/>
                    <circle cx="16" cy="6" r="2"/>
                    <circle cx="8" cy="12" r="2"/>
                    <circle cx="16" cy="12" r="2"/>
                    <circle cx="8" cy="18" r="2"/>
                    <circle cx="16" cy="18" r="2"/>
                  </svg>
                </div>

                {!isLiveChat && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      const newLang = language === "en" ? "tl" : "en";
                      setLanguage(newLang);
                      setMessages((prev) => [
                        ...prev,
                        {
                          id: Date.now(),
                          sender: "bot",
                          text: newLang === "tl" ? "Pinalitan ang wika sa Tagalog! Paano ko matutulungan ang inyong alaga ngayon? 🐾" : "Language switched to English! How can I help with your pet today? 🐾",
                          time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
                        },
                      ]);
                    }}
                    className="text-xs font-black bg-white/20 hover:bg-white/30 transition-colors px-2.5 py-1 rounded-lg cursor-pointer uppercase shadow-sm"
                    title="Switch Language"
                  >
                    {language === "en" ? "EN 🇺🇸" : "TL 🇵🇭"}
                  </button>
                )}
                {isLiveChat && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEndLiveChat();
                    }}
                    className="text-xs font-black bg-white/20 hover:bg-red-500 transition-colors px-2.5 py-1 rounded-lg cursor-pointer"
                    title="End Live Chat"
                  >
                    End Chat
                  </button>
                )}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsOpen(false);
                  }}
                  className="text-white hover:bg-white/30 transition-colors bg-white/20 w-8 h-8 rounded-full flex items-center justify-center font-bold cursor-pointer text-lg leading-none"
                >
                  ×
                </button>
              </div>
            </div>

            {/* Messages Area */}
            <div className="flex-1 overflow-y-auto p-4 bg-[#faf9f8] flex flex-col gap-3.5">
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`message-anim flex flex-col max-w-[85%] ${msg.sender === "user" ? "self-end items-end" : "self-start items-start"}`}
                >
                  {msg.sender === "admin" && (
                    <span className="text-[10px] text-emerald-700 font-black mb-0.5 px-1 flex items-center gap-1">
                      🧑‍💼 Live Admin
                    </span>
                  )}
                  {msg.sender === "bot" && (
                    <span className="text-[10px] text-purple-700 font-black mb-0.5 px-1 flex items-center gap-1">
                      🐾 FurBot
                    </span>
                  )}
                  <div
                    className={`px-4 py-2.5 rounded-2xl text-xs sm:text-sm leading-relaxed shadow-sm whitespace-pre-line ${
                      msg.sender === "user"
                        ? "bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-tr-xs"
                        : msg.sender === "admin"
                        ? "bg-emerald-50 text-gray-800 border border-emerald-200 rounded-tl-xs"
                        : "bg-white text-gray-800 border border-[#e8e4df] rounded-tl-xs"
                    }`}
                  >
                    {msg.text}

                    {/* Interactive One-Tap Action Button */}
                    {msg.suggestedTab && (
                      <button
                        onClick={() => navigateToTab(msg.suggestedTab)}
                        className="mt-2.5 flex items-center gap-1.5 px-3.5 py-1.5 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold text-xs rounded-xl shadow-md transition-all hover:scale-105 active:scale-95 cursor-pointer"
                      >
                        <span>
                          👉 {msg.suggestedTab === "booking"
                            ? (language === "tl" ? "Pumunta sa Booking Form" : "Open Booking Form")
                            : msg.suggestedTab === "products"
                            ? (language === "tl" ? "Tingnan ang Products Catalog" : "View Products Catalog")
                            : msg.suggestedTab === "services"
                            ? (language === "tl" ? "Tingnan ang Services" : "View Services")
                            : (language === "tl" ? "Pumunta sa Contact Page" : "Go to Contact Page")}
                        </span>
                        <span>🐾</span>
                      </button>
                    )}
                  </div>
                  <span className="text-[9px] text-gray-400 mt-1 px-1 font-bold">{msg.time}</span>
                </div>
              ))}

              {/* Animated Typing Indicator */}
              {isTyping && (
                <div className="message-anim flex flex-col max-w-[85%] self-start items-start">
                  <span className="text-[10px] text-purple-700 font-bold mb-1 px-1 flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-purple-500 animate-ping inline-block"></span>
                    🐾 FurBot is typing...
                  </span>
                  <div className="px-4 py-3 rounded-2xl bg-white border border-purple-200 shadow-md flex items-center gap-2 rounded-tl-xs">
                    <span className="w-2.5 h-2.5 rounded-full bg-gradient-to-r from-purple-600 to-pink-500 typing-dot" style={{ animationDelay: "0ms" }}></span>
                    <span className="w-2.5 h-2.5 rounded-full bg-gradient-to-r from-purple-600 to-pink-500 typing-dot" style={{ animationDelay: "200ms" }}></span>
                    <span className="w-2.5 h-2.5 rounded-full bg-gradient-to-r from-purple-600 to-pink-500 typing-dot" style={{ animationDelay: "400ms" }}></span>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Interactive Suggestion Chips */}
            {!isLiveChat && (
              <div className="px-3 pt-2 pb-1.5 bg-white border-t border-gray-100 flex gap-1.5 overflow-x-auto no-scrollbar">
                {promptChips.map((chip, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleSend(null, chip.query)}
                    className="whitespace-nowrap px-2.5 py-1 bg-purple-50 hover:bg-purple-100 text-purple-700 font-bold text-[11px] rounded-full transition-all border border-purple-100 shadow-2xs hover:scale-105 active:scale-95 cursor-pointer flex-shrink-0"
                  >
                    {chip.label}
                  </button>
                ))}
              </div>
            )}

            {/* Input Area */}
            <div className="p-3 bg-white border-t border-gray-100">
              {isLiveChat && !adminOnline && (
                <p className="text-[11px] text-center text-amber-600 font-bold mb-2 animate-pulse">
                  ⏳ {language === "tl" ? "Naghihintay sa live admin na kumonekta..." : "Waiting for a live admin to connect..."}
                </p>
              )}
              <form onSubmit={(e) => handleSend(e)} className="flex gap-2">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder={
                    isLiveChat
                      ? (adminOnline ? (language === "tl" ? "I-type ang iyong mensahe..." : "Type your message...") : (language === "tl" ? "Naghihintay sa admin..." : "Waiting for admin..."))
                      : (language === "tl" ? "Magtanong tungkol sa Furpect Tails..." : "Ask about Furpect Tails services, prices...")
                  }
                  disabled={isLiveChat && !adminOnline}
                  className="flex-1 px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-xs sm:text-sm focus:outline-none focus:border-purple-500 focus:bg-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                />
                <button
                  type="submit"
                  disabled={!input.trim() || (isLiveChat && !adminOnline)}
                  className={`text-white w-10 h-10 rounded-xl flex items-center justify-center shadow-md transition-all hover:scale-105 active:scale-95 disabled:opacity-40 disabled:hover:scale-100 cursor-pointer ${
                    isLiveChat ? "bg-emerald-600 hover:bg-emerald-700" : "bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                  }`}
                >
                  <svg className="w-5 h-5 ml-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                </button>
              </form>
            </div>
          </div>
        );
      })()}

      {/* FAB Floating Button (Draggable anywhere on screen) */}
      <div
        style={{
          position: "fixed",
          left: `${fabPos.x}px`,
          top: `${fabPos.y}px`,
          zIndex: 1000,
          touchAction: "none",
        }}
        className="font-sans select-none"
      >
        <div className="relative">
          {/* Tooltip bubble */}
          {!isOpen && (
            <div className="chat-bubble-tooltip absolute bottom-full right-0 mb-3 whitespace-nowrap pointer-events-none">
              <div className="bg-white text-gray-800 text-xs font-bold px-3.5 py-1.5 rounded-full shadow-xl border border-purple-100 flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse inline-block"></span>
                <span>Furpect Tails Assistant 🐾</span>
                <span className="text-[10px] text-purple-600 font-semibold bg-purple-50 px-1.5 py-0.5 rounded-md">
                  
                </span>
              </div>
              <div className="w-3 h-3 bg-white border-r border-b border-purple-100 rotate-45 ml-auto mr-5 -mt-1.5 shadow-xs"></div>
            </div>
          )}

          {/* Pulse rings */}
          {!isOpen && !isLiveChat && !isFabDragging && (
            <>
              <span className="pulse-ring" />
              <span className="pulse-ring2" />
            </>
          )}

          {/* Live chat indicator */}
          {isLiveChat && !isOpen && (
            <span className="absolute -top-1 -right-1 z-10 w-4 h-4 bg-emerald-500 rounded-full border-2 border-white">
              <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping"></span>
            </span>
          )}

          <button
            onMouseDown={handleFabPointerDown}
            onTouchStart={handleFabPointerDown}
            onClick={handleFabClick}
            className={`fab-btn relative w-16 h-16 rounded-full flex items-center justify-center shadow-2xl overflow-hidden cursor-grab active:cursor-grabbing ${
              isFabDragging ? "scale-110 shadow-purple-500/50 ring-4 ring-purple-300" : ""
            } ${
              isOpen
                ? "bg-gray-800 text-white"
                : isLiveChat
                ? "bg-gradient-to-br from-emerald-500 to-teal-500 text-white"
                : "bg-gradient-to-br from-purple-600 via-fuchsia-500 to-pink-500"
            }`}
            title={isOpen ? "Close Chat" : "Drag to move or click to chat"}
          >
            {isOpen ? (
              <span className="text-3xl font-bold pointer-events-none" style={{ lineHeight: 1 }}>×</span>
            ) : (
              <DogBotIcon size={64} className={`${isFabDragging ? "" : "dog-bounce"} pointer-events-none`} />
            )}
          </button>
        </div>
      </div>
    </>
  );
};

export default Chatbot;
